// Fleet settings — operator-authed credentials shared by every Convenely site.
//
//   GET    /store/settings        -> { settings: FleetSetting[] }  (names only)
//   POST   /store/settings        -> FleetSetting                  (set / rotate)
//   DELETE /store/settings/{name} -> { name, deleted }
//
// There is deliberately NO read hook for a value: GET /store/settings/{name} is
// local-access-key authed and belongs to the gateway, not the portal. The
// portal can prove a credential is set and replace it; it can never read it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../lib/apiContext';
import { queryKeys } from '../../lib/queryKeys';
import type {
  FleetSetting,
  FleetSettingsResponse,
  SetSettingRequest,
  DeleteSettingResponse,
} from '../../lib/types';

/** GET /store/settings — which fleet settings are configured, and when last changed. */
export function useFleetSettings() {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.fleetSettings,
    queryFn: () => client.get<FleetSettingsResponse>('/store/settings'),
  });
}

/** POST /store/settings — set or rotate. Setting an existing name IS the rotation. */
export function useSetFleetSetting() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetSettingRequest) =>
      client.post<FleetSetting>('/store/settings', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.fleetSettings }),
  });
}

/** DELETE /store/settings/{name} — remove a fleet setting entirely. */
export function useDeleteFleetSetting() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      client.delete<DeleteSettingResponse>(`/store/settings/${encodeURIComponent(name)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.fleetSettings }),
  });
}
